import React from 'react';
import { styled } from '@components-shared/src/stitches';
import { usePostsEnabled } from '@components/Feed/Post/usePostsEnabled';
import {
  ScrollViewport,
  StyledScrollArea,
  StyledScrollbar,
  StyledThumb,
} from '@components/main-components/containers/RadixScroll';
import { Chip } from '@components/main-components/elements/chips/Chip';
import { ReleaseActivityFeedTypeFilterOption } from '@graphql/reactQuery';
import { multiSelectOptionIsSelected, selectMultiSelectOption } from '@utils/helpers';

type FeedType = {
  type: ReleaseActivityFeedTypeFilterOption;
  label: string;
  icon?: React.ReactNode;
}[];

const FEED_TYPES = (activityFeedV2Enabled: boolean): FeedType => [
  {
    type: ReleaseActivityFeedTypeFilterOption.All,
    label: 'All',
  },
  ...(activityFeedV2Enabled
    ? [
        {
          type: ReleaseActivityFeedTypeFilterOption.Posts,
          label: 'Posts',
        },
      ]
    : []),
  {
    type: ReleaseActivityFeedTypeFilterOption.Collections,
    label: 'Collected',
  },
  {
    type: ReleaseActivityFeedTypeFilterOption.Playlists,
    label: 'Playlists',
  },
  {
    type: ReleaseActivityFeedTypeFilterOption.Likes,
    label: 'Likes',
  },
  // Not showing releases here because there will always be just 1,
  // but we will show releases in all or if only a few filters are deselected
];

export const FeedHeader = ({
  filter,
  setFilter,
  icons,
}: {
  filter: ReleaseActivityFeedTypeFilterOption[];
  setFilter: (selection: ReleaseActivityFeedTypeFilterOption[]) => void;
  icons: JSX.Element;
}) => {
  const { viewingPostsEnabled } = usePostsEnabled();
  return (
    <Filter>
      <RestyledScrollArea>
        <ScrollViewport>
          <FilterContainer>
            {FEED_TYPES(viewingPostsEnabled).map(({ type, label }) => (
              <ChipContainer key={type}>
                <Chip
                  text={label}
                  isSelected={multiSelectOptionIsSelected({
                    prev: filter || [],
                    currentSelected: type,
                    all: Object.values(ReleaseActivityFeedTypeFilterOption),
                    allKey: ReleaseActivityFeedTypeFilterOption.All,
                  })}
                  onClick={() => {
                    setFilter(
                      selectMultiSelectOption({
                        prev: filter || [],
                        currentSelected: type,
                        all: Object.values(ReleaseActivityFeedTypeFilterOption),
                        allKey: ReleaseActivityFeedTypeFilterOption.All,
                      }),
                    );
                  }}
                />
              </ChipContainer>
            ))}
          </FilterContainer>
        </ScrollViewport>
        <StyledScrollbar hiddenOnMobile orientation="horizontal">
          <StyledThumb hiddenOnMobile />
        </StyledScrollbar>
      </RestyledScrollArea>
      <Icons>{icons}</Icons>
    </Filter>
  );
};

const Filter = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'hidden',
  margin: '-12px 0 10px',
  padding: '0 16px',

  h3: {
    kicker: 'S',
    fontStyle: 'regular',
    textTransform: 'uppercase',
  },

  '@tablet': {
    flexDirection: 'row',
    alignItems: 'center',
  },

  '@desktop': {
    margin: '2px 0 21px',
    padding: '0',
  },
});

const FilterContainer = styled('div', {
  display: 'flex',
});

const RestyledScrollArea = styled(StyledScrollArea, {
  marginLeft: -16,
  paddingLeft: 0,
  width: 'calc(100% + 32px)',
  '@tablet': {
    width: 'fit-content',
  },
  '@desktop': {
    paddingLeft: 16,
  },
});

const ChipContainer = styled('div', {
  width: 'fit-content',
  marginX: 4,
  paddingY: 8,

  '&:first-child': {
    marginLeft: 0,
  },
  '&:last-child': {
    marginRight: '2rem',
  },
  '@tablet': {
    paddingY: 0,
  },
});

const Icons = styled('div', {
  marginLeft: 'auto',
});
